import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  on,
  onCleanup,
  Show,
  Switch,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { mapSemanticSearchError } from "@/api/utils/semantic-search-error";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import type { useInfiniteSearchFreelancers } from "../api";
import { ANTI_FLASH_GATE_MS } from "../hooks/useSearchRequestClock";
import type { FreelancerSearchResult } from "../types";
import {
  type PreservedResults,
  resolveResultsDisplay,
} from "../utils/results-display";
import { FreelanceCard } from "./FreelanceCard";
import { SearchLoadingSteps } from "./SearchLoadingSteps";

type SearchQuery = ReturnType<typeof useInfiniteSearchFreelancers>;

/* La rise dura 300ms + 120ms di stagger massimo: oltre questa finestra la
   classe d'entrata si toglie, così le card delle ricerche successive non la
   rigiocano mai (D15). */
const ENTRANCE_CLEAR_MS = 600;
const CARD_STAGGER_MS = 60;

/**
 * Finestra minima di permanenza della loading screen (E-1). Il gate anti-flash
 * decide SE la schermata compare; questa decide che, una volta comparsa, resta
 * abbastanza da essere letta: senza, una risposta a ~300ms la fa apparire e
 * sparire in poche decine di ms — un lampo, non una narrazione.
 */
export const LOADING_MIN_VISIBLE_MS = 400;

export type SearchResultsPanelProps = {
  // The route owns the query/filters/sheet/selection; this panel only renders.
  search: SearchQuery;
  /**
   * Istante di partenza della richiesta di prima ricerca, posseduto dalla
   * route: sopravvive al giro di tab (E-3) e ri-ancora la narrazione sul
   * retry post-errore (D12).
   */
  startedAt: number;
  /**
   * Ultima lista buona, posseduta dalla route: su errore di una ricerca
   * successiva la query molla i dati (`keepPreviousData` non sopravvive al
   * fallimento) e il panel rende questo snapshot con l'etichetta "ricerca
   * precedente" (AC11/F-4).
   */
  lastGood: PreservedResults | null;
  /**
   * Segnale di ricerca-in-corso per volontà dell'utente (AC8), acceso dalla
   * route alla conferma (Applica/Reset/retry), pre-debounce — mai derivato
   * da `isFetching` (D17/E-8).
   */
  busy: boolean;
  /**
   * C'è almeno un freelance selezionato, quindi la barra di selezione flottante
   * occupa la cima del viewport: l'indicatore di ricerca si sposta sotto di lei
   * invece di finirci nascosto dietro (E-11/AC9). Arriva dalla route perché lo
   * store della ricerca lancia senza provider e il panel deve restare
   * renderizzabile da solo.
   */
  hasSelection: boolean;
  /**
   * Notifica alla route il retry dal banner con lista conservata: è una
   * conferma utente e accende attenuazione+indicatore (D12).
   */
  onPreservedRetry: () => void;
  /** Via d'uscita dallo stato vuoto: azzera i filtri e rilancia la ricerca. */
  onResetFilters: () => void;
  onViewProfile: (freelancer: FreelancerSearchResult) => void;
  onBackToSearch: () => void;
};

/**
 * The freelancer search results region — count subtitle, the `FreelanceCard`
 * list, load-more, and the no-results fallback. Extracted verbatim from the
 * result route (AC3: search behavior unchanged). Owned state (query, filters,
 * sheet, selection) stays in the route and flows in via props.
 *
 * The per-tab count lives HERE, in the panel's content header (AC7, lazy-safe) —
 * never on the segmented-control trigger label.
 */
export const SearchResultsPanel: Component<SearchResultsPanelProps> = (
  props,
) => {
  const { t } = useI18n();

  /* ⚠ solid-query sospende sull'accesso a `data` durante la prima fetch:
     dentro la Suspense del router mostrerebbe lo skeleton di app al posto
     della loading screen (AC1). Ogni lettura di `data` resta quindi dietro
     `isPending` (pending iniziale ⇒ nessun dato: si risponde senza toccare
     la resource; il placeholder dei refine ha `isPending === false`). */
  const freelancers = createMemo(() =>
    props.search.isPending
      ? []
      : (props.search.data?.pages.flatMap((p) => p.data) ?? []),
  );
  const totalResults = createMemo(() =>
    props.search.isPending
      ? 0
      : (props.search.data?.pages?.[0]?.pagination?.total ?? 0),
  );
  const error = createMemo(() =>
    props.search.error ? mapSemanticSearchError(props.search.error) : undefined,
  );

  /* Stato dell'area risultati (resolver puro): loading screen solo con fetch
     in volo e zero dati mostrabili (D12); su errore con snapshot presente la
     lista conservata vince sulla loading screen (AC11). */
  const display = createMemo(() =>
    resolveResultsDisplay({
      status: props.search.status,
      fetchStatus: props.search.fetchStatus,
      // isPending prima di data: mai toccare la resource in pending iniziale.
      hasData: !props.search.isPending && props.search.data !== undefined,
      lastGood: props.lastGood != null,
    }),
  );

  /* La loading screen richiesta dallo stato, prima della finestra minima. */
  const wantsLoadingScreen = createMemo(() => display() === "loading");

  /* E-1: la finestra minima si apre quando la schermata compare DAVVERO —
     `SearchLoadingSteps` non disegna nulla nei primi `ANTI_FLASH_GATE_MS`
     dal `startedAt` — e non prima. Se la risposta arriva dentro il gate la
     finestra non si apre mai: nessuna attesa artificiale, nessun lampo. */
  const [minWindowOpen, setMinWindowOpen] = createSignal(false);
  let releaseTimer: number | undefined;
  onCleanup(() => window.clearTimeout(releaseTimer));
  createEffect(() => {
    if (!wantsLoadingScreen()) return;
    const appearsIn = Math.max(
      0,
      ANTI_FLASH_GATE_MS - (Date.now() - props.startedAt),
    );
    const appearTimer = window.setTimeout(() => {
      // Una nuova comparsa fa ripartire la finestra: mai due scadenze in volo.
      window.clearTimeout(releaseTimer);
      setMinWindowOpen(true);
      releaseTimer = window.setTimeout(
        () => setMinWindowOpen(false),
        LOADING_MIN_VISIBLE_MS,
      );
    }, appearsIn);
    /* Si annulla solo l'attesa della comparsa: la scadenza della finestra deve
       maturare proprio DOPO che la risposta è arrivata — è il suo scopo. */
    onCleanup(() => window.clearTimeout(appearTimer));
  });

  /* La finestra minima trattiene la schermata solo verso i risultati: se la
     ricerca finisce in errore il banner deve comparire subito, e schermata +
     banner insieme violerebbero la mutua esclusione AC4/D12. */
  const isLoadingScreenVisible = createMemo(
    () => wantsLoadingScreen() || (minWindowOpen() && display() === "live"),
  );

  /* La lista e il conteggio da mostrare: live (freschi o placeholder) oppure
     lo snapshot conservato — un'unica sorgente derivata per il <For>. */
  const displayFreelancers = createMemo(() =>
    display() === "stale-preserved"
      ? (props.lastGood?.freelancers ?? [])
      : freelancers(),
  );
  const displayTotal = createMemo(() =>
    display() === "stale-preserved"
      ? (props.lastGood?.total ?? 0)
      : totalResults(),
  );

  /* Mutua esclusione AC4/D12: mai un banner d'errore mentre un fetch è in
     volo (loading screen o retry sulla lista conservata) né dopo una nuova
     conferma dell'utente (l'errore riguarda un tentativo superato). */
  const visibleError = createMemo(() =>
    props.search.fetchStatus === "fetching" || props.busy ? undefined : error(),
  );

  /* Attenuazione e indicatore: solo quando il busy convive con contenuti
     mostrabili — durante la loading screen l'attesa è già raccontata lì. */
  const busySignalOn = createMemo(
    () => props.busy && !isLoadingScreenVisible(),
  );

  /* E-24: due soli annunci SR — "ricerca in corso" alla conferma e uno
     all'arrivo dei nuovi risultati (sull'errore parla il banner, role=alert). */
  const [busyAnnounce, setBusyAnnounce] = createSignal<
    "none" | "searching" | "updated"
  >("none");
  createEffect(
    on(
      () => props.busy,
      (busy, prev) => {
        if (busy) setBusyAnnounce("searching");
        else if (prev && !props.search.error) setBusyAnnounce("updated");
        else setBusyAnnounce("none");
      },
      { defer: true },
    ),
  );

  /* Entrata dei risultati SOLO nella transizione prima-ricerca→risultati:
     rise sulle card (stagger 0/60/120ms, solo prime 3) e fade sul conteggio.
     La classe si toglie a fine finestra così i refine non la rigiocano (D15). */
  /* Accumulatore esplicito, NON `on(..., {defer:true})`: il panel monta a
     loading già visibile, quindi il primo scatto del memo arriverebbe con
     `prev === undefined` e l'entrata non partirebbe mai. */
  const [entranceActive, setEntranceActive] = createSignal(false);
  createEffect<boolean>((wasLoading) => {
    const visible = isLoadingScreenVisible();
    if (wasLoading && !visible && untrack(() => freelancers().length > 0)) {
      setEntranceActive(true);
      /* E-24: l'arrivo dei risultati è il secondo annuncio che conta, e sullo
         stesso fronte. Serve qui perché `props.busy` non si accende sulla prima
         ricerca (arriva per navigazione) e perché la `role="status"` viva della
         loading screen esce dal DOM senza annunciare nulla. Sul percorso di
         conferma si tace: lì il fronte busy→false annuncia già, e il secondo
         sarebbe uno di troppo. */
      if (untrack(() => !props.busy)) setBusyAnnounce("updated");
      const timeout = window.setTimeout(
        () => setEntranceActive(false),
        ENTRANCE_CLEAR_MS,
      );
      onCleanup(() => window.clearTimeout(timeout));
    }
    return visible;
  }, false);

  /* "Carica altri" (E-12): il blocco ESISTE solo sullo stato live con una
     pagina successiva. Quando manca per uno di questi due motivi non c'era
     nulla prima, e smontarlo non sposta una riga. */
  const hasLoadMoreSlot = createMemo(
    () => display() === "live" && props.search.hasNextPage === true,
  );
  /* Durante il busy (o un fetch in volo) il bottone se ne va ma lo spazio
     resta: smontare ~130px in-flow accorcerebbe il documento sotto il cursore
     di chi conferma dalla sidebar stando in fondo alla lista (AC13). */
  const isLoadMoreReady = createMemo(
    () => hasLoadMoreSlot() && !props.busy && !props.search.isFetching,
  );

  const handleLoadMore = () => {
    if (props.search.hasNextPage) props.search.fetchNextPage();
  };

  const handleRetry = () => {
    // D12: il retry con lista conservata è una conferma utente — la route
    // accende attenuazione+indicatore; senza dati riparte la loading screen.
    if (display() === "stale-preserved") props.onPreservedRetry();
    return props.search.isFetchNextPageError
      ? props.search.fetchNextPage()
      : props.search.refetch();
  };

  return (
    <div class="space-y-4">
      <Show when={isLoadingScreenVisible()}>
        <SearchLoadingSteps startedAt={props.startedAt} />
      </Show>

      <Show when={visibleError()}>
        {(mapped) => {
          const retryAfter = () => {
            const current = mapped();
            return current.kind === "rate_limited"
              ? current.retryAfterSeconds
              : undefined;
          };
          const genericMessage = () => {
            const current = mapped();
            return current.kind === "generic"
              ? current.message ||
                  t("company.search.freelancers.result.semantic_errors.generic")
              : "";
          };
          const rateLimitMessage = () =>
            retryAfter()
              ? t(
                  "company.search.freelancers.result.semantic_errors.rate_limited_with_retry",
                  { seconds: retryAfter() ?? 0 },
                )
              : t(
                  "company.search.freelancers.result.semantic_errors.rate_limited",
                );
          return (
            <div
              class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              role="alert"
            >
              <Switch>
                <Match when={mapped().kind === "rate_limited"}>
                  {rateLimitMessage()}
                </Match>
                <Match when={mapped().kind === "semantic_unavailable"}>
                  {t(
                    "company.search.freelancers.result.semantic_errors.unavailable",
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    class="ml-3"
                    onClick={handleRetry}
                  >
                    {t(
                      "company.search.freelancers.result.semantic_errors.retry",
                    )}
                  </Button>
                </Match>
                <Match when={mapped().kind === "invalid_query"}>
                  {t(
                    "company.search.freelancers.result.semantic_errors.invalid_query",
                  )}
                </Match>
                <Match when={mapped().kind === "invalid_offer"}>
                  {t(
                    "company.search.freelancers.result.semantic_errors.invalid_offer",
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    class="ml-3"
                    onClick={() => props.onBackToSearch()}
                  >
                    {t(
                      "company.search.freelancers.result.semantic_errors.back_to_offers",
                    )}
                  </Button>
                </Match>
                <Match when={mapped().kind === "timeout"}>
                  {t(
                    "company.search.freelancers.result.semantic_errors.timeout",
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    class="ml-3"
                    onClick={handleRetry}
                  >
                    {t(
                      "company.search.freelancers.result.semantic_errors.retry",
                    )}
                  </Button>
                </Match>
                <Match when={mapped().kind === "generic"}>
                  {genericMessage()}
                  <Button
                    variant="outline"
                    size="sm"
                    class="ml-3"
                    onClick={handleRetry}
                  >
                    {t(
                      "company.search.freelancers.result.semantic_errors.retry",
                    )}
                  </Button>
                </Match>
              </Switch>
            </div>
          );
        }}
      </Show>

      {/* E-24: unico canale di annuncio SR per il busy — due annunci in
          tutto, conferma e arrivo. L'indicatore visivo è aria-hidden. */}
      <p class="sr-only" role="status" data-testid="search-announce">
        <Switch fallback="">
          <Match when={busyAnnounce() === "searching"}>
            {t("company.search.freelancers.result.searching")}
          </Match>
          <Match when={busyAnnounce() === "updated"}>
            {t("company.search.freelancers.result.search_updated")}
          </Match>
        </Switch>
      </p>

      <div class="relative">
        {/* Indicatore esplicito (AC9, D14): sempre montato, fuori dal
            wrapper attenuato, sticky così resta visibile da posizione
            scrollata (UX pass §3). pointer-events-none: le card sotto
            restano cliccabili (AC13). Vive fuori da ogni <Show> condizionale
            sul conteggio: deve sopravvivere alla lista vuota. */}
        <div
          class="pointer-events-none absolute inset-0 z-10"
          aria-hidden="true"
        >
          {/* E-11: con la selezione attiva la barra flottante occupa la stessa
              fascia — `fixed top-4`, z-40, fondo opaco, alta 62px (py-3 + gli
              avatar h-9 + il bordo), quindi chiude a 78px dal bordo. Sotto
              quella soglia il chip sparirebbe dietro di lei da qualunque
              posizione scrollata, lasciando la sola attenuazione della lista —
              cioè esattamente ciò che AC9 esiste per evitare. */}
          <div
            data-busy={busySignalOn() ? "true" : "false"}
            class="search-busy-indicator sticky mx-auto flex w-fit items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground shadow-sm"
            classList={{
              "top-2": !props.hasSelection,
              "top-22": props.hasSelection,
            }}
          >
            <span class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary/30 border-t-primary motion-reduce:animate-none" />
            {t("company.search.freelancers.result.searching")}
          </div>
        </div>

        {/* Wrapper di attenuazione (D14/D17): sempre montato, conteggio e
            lista cambiano stato insieme (F-3). */}
        <div
          data-busy={busySignalOn() ? "true" : "false"}
          aria-busy={props.busy}
          class="search-busy-wrapper space-y-4"
        >
          {/* Durante la finestra minima la schermata di attesa è ancora su a
              dati già arrivati: i risultati aspettano il loro turno, o si
              vedrebbero le due viste impilate. */}
          <Show when={!isLoadingScreenVisible()}>
            <Show when={display() === "stale-preserved"}>
              {/* gray-600, non gray-500: attenuato allo 0.85 il primo sta a
                  4.85:1, il secondo a 3.39:1 — sotto AA (WCAG 1.4.3) su
                  contenuto che resta leggibile e cliccabile. */}
              <p class="text-sm font-medium text-gray-600">
                {t("company.search.freelancers.result.previous_results")}
              </p>
            </Show>

            <Show when={displayTotal() > 0}>
              <p
                class="text-gray-600"
                classList={{ "search-fade-in": entranceActive() }}
              >
                {t("company.search.freelancers.result.subtitle", {
                  count: displayTotal(),
                })}
              </p>
            </Show>

            <Show
              when={props.search.isSuccess || displayFreelancers().length > 0}
            >
              <Show
                when={displayFreelancers().length > 0}
                fallback={
                  /* Lo stato vuoto non è un punto morto (error path «la ricerca
                     successiva non torna nulla»): accanto al messaggio c'è la
                     via d'uscita, come in `MatchEmptyFiltered` del Talent Pool
                     (stesso pattern, copy propria — quel componente è di un
                     altro dominio e resta intoccato). */
                  <div class="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center">
                    <div class="flex max-w-md flex-col gap-2">
                      <p class="text-gray-600">
                        {t("company.search.freelancers.result.no_results")}
                      </p>
                      {/* `gray-600` e non `text-muted-foreground`: misurato sul
                          reale, quel token sta a 4.40:1 sullo sfondo pagina —
                          sotto AA (WCAG 1.4.3) già a piena opacità, prima
                          ancora dell'attenuazione. gray-600 sta a 6.99:1. */}
                      <p class="text-sm leading-relaxed text-gray-600">
                        {t("company.search.freelancers.result.no_results_hint")}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => props.onResetFilters()}
                    >
                      {t("company.search.freelancers.result.no_results_reset")}
                    </Button>
                  </div>
                }
              >
                <For each={displayFreelancers()}>
                  {(freelancer, index) => {
                    return (
                      <div
                        classList={{ "search-rise": entranceActive() }}
                        style={{
                          "animation-delay": entranceActive()
                            ? `${Math.min(index(), 2) * CARD_STAGGER_MS}ms`
                            : undefined,
                        }}
                      >
                        <FreelanceCard
                          freelancer={freelancer}
                          onViewProfile={() => props.onViewProfile(freelancer)}
                        />
                      </div>
                    );
                  }}
                </For>
                {/* E-12: "Carica altri" solo sullo stato live e mai durante il
                busy — con la lista conservata `hasNextPage` appartiene alla
                ricerca fallita. Quando sparisce per il busy resta il suo
                ingombro (AC13), non un buco. */}
                <Show when={hasLoadMoreSlot()}>
                  <Show
                    when={isLoadMoreReady()}
                    fallback={<div class="h-24 mt-4 mb-4" aria-hidden="true" />}
                  >
                    <div class="flex flex-col items-center justify-center h-24 border-t border-gray-300 mt-4 mb-4">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={handleLoadMore}
                        disabled={props.search.isFetchingNextPage}
                      >
                        {t("company.search.freelancers.result.next_page")}
                      </Button>
                    </div>
                  </Show>
                </Show>
              </Show>
            </Show>
          </Show>
        </div>
      </div>
      {/* E-12: lo spinner di paginazione non convive col segnale busy — chip
          "ricerca in corso" in alto e spinner in fondo racconterebbero due
          attese diverse per la stessa lista. */}
      <Show when={props.search.isFetchingNextPage && !props.busy}>
        <Loading />
      </Show>
    </div>
  );
};
